(() => {
  const SELECTOR = '[data-bcj-announcement-bar]';
  const HIDDEN_CLASS = 'is-hidden';
  const ROTATING_CLASS = 'is-rotating';
  const ACTIVE_CLASS = 'is-active';
  const PAUSED_CLASS = 'is-paused';
  const ROOT_HIDDEN_CLASS = 'bcj-announcement-hidden';
  const MIN_SCROLL_DELTA = 8;
  const TOP_REVEAL_OFFSET = 8;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initBar = (bar) => {
    if (!bar || bar.dataset.bcjAnnouncementInited === 'true') return;
    bar.dataset.bcjAnnouncementInited = 'true';

    const slides = Array.from(bar.querySelectorAll('.announcement-slider__slide'));
    const pauseButton = bar.querySelector('.slideshow__pause');
    const speed = Number(bar.dataset.rotationSpeed || 4000);
    const hideOnScroll = bar.dataset.hideOnScroll !== 'false';
    const motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains(ACTIVE_CLASS)));
    let timer = null;
    let lastScrollY = window.scrollY || 0;
    let touchStartY = null;
    let ticking = false;
    let userPaused = false;
    let pointerPaused = false;
    let focusPaused = false;
    let scrollHidden = false;
    let resizeObserver = null;
    let isActive = true;

    const syncHeight = () => {
      const height = Math.ceil(bar.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty('--bcj-announcement-height', `${height}px`);
    };

    const showSlide = (index) => {
      if (!slides.length) return;
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle(ACTIVE_CLASS, isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.inert = !isActive;
      });
      window.requestAnimationFrame(syncHeight);
    };

    const canRotate = () =>
      slides.length > 1 &&
      !prefersReducedMotion() &&
      !userPaused &&
      !pointerPaused &&
      !focusPaused &&
      !scrollHidden &&
      !document.hidden;

    const startRotation = () => {
      if (timer || !canRotate()) return;
      bar.classList.add(ROTATING_CLASS);
      timer = window.setInterval(() => showSlide(activeIndex + 1), speed);
    };

    const stopRotation = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const updateRotation = () => {
      if (canRotate()) startRotation();
      else stopRotation();
    };

    const setHidden = (shouldHide) => {
      if (shouldHide && bar.contains(document.activeElement)) return;
      scrollHidden = Boolean(shouldHide);
      bar.classList.toggle(HIDDEN_CLASS, Boolean(shouldHide));
      bar.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
      bar.inert = Boolean(shouldHide);
      document.documentElement.classList.toggle(ROOT_HIDDEN_CLASS, Boolean(shouldHide));
      updateRotation();
    };

    const toggleUserPause = () => {
      userPaused = !userPaused;
      bar.classList.toggle(PAUSED_CLASS, userPaused);
      if (pauseButton) {
        const label = userPaused ? pauseButton.dataset.playLabel : pauseButton.dataset.pauseLabel;
        if (label) pauseButton.setAttribute('aria-label', label);
        pauseButton.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      }
      updateRotation();
    };

    const handlePointerEnter = () => { pointerPaused = true; updateRotation(); };
    const handlePointerLeave = () => { pointerPaused = false; updateRotation(); };
    const handleFocusIn = () => { focusPaused = true; updateRotation(); };
    const handleFocusOut = (event) => {
      if (event.relatedTarget && bar.contains(event.relatedTarget)) return;
      focusPaused = false;
      updateRotation();
    };
    const handleVisibilityChange = () => updateRotation();
    const handleMotionChange = () => updateRotation();

    const updateOnScroll = () => {
      ticking = false;
      if (!hideOnScroll) return;

      const currentY = Math.max(0, window.scrollY || 0);
      const delta = currentY - lastScrollY;

      if (currentY <= TOP_REVEAL_OFFSET) {
        setHidden(false);
      } else if (delta > MIN_SCROLL_DELTA) {
        setHidden(true);
      } else if (delta < -MIN_SCROLL_DELTA) {
        setHidden(false);
      }

      lastScrollY = currentY;
    };

    const requestScrollUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateOnScroll);
    };

    const handleTouchStart = (event) => {
      touchStartY = event.touches && event.touches.length ? event.touches[0].clientY : null;
    };
    const handleTouchMove = (event) => {
      if (touchStartY === null || !event.touches || !event.touches.length) return;
      const touchY = event.touches[0].clientY;
      const swipeDelta = touchStartY - touchY;
      if (Math.abs(swipeDelta) < 18) return;
      setHidden(swipeDelta > 0);
    };
    const handleTouchEnd = () => { touchStartY = null; };

    showSlide(activeIndex);
    syncHeight();
    startRotation();
    window.addEventListener('resize', syncHeight);
    bar.addEventListener('mouseenter', handlePointerEnter);
    bar.addEventListener('mouseleave', handlePointerLeave);
    bar.addEventListener('focusin', handleFocusIn);
    bar.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (pauseButton) {
      pauseButton.setAttribute('aria-pressed', 'false');
      pauseButton.addEventListener('click', toggleUserPause);
    }
    if (motionQuery) {
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', handleMotionChange);
      else if (motionQuery.addListener) motionQuery.addListener(handleMotionChange);
    }
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(syncHeight);
      resizeObserver.observe(bar);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (isActive) syncHeight(); });
    }

    if (hideOnScroll) {
      window.addEventListener('scroll', requestScrollUpdate, { passive: true });
      window.addEventListener('touchstart', handleTouchStart, { passive: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target || !event.target.contains(bar)) return;
      isActive = false;
      stopRotation();
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', syncHeight);
      window.removeEventListener('scroll', requestScrollUpdate);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      bar.removeEventListener('mouseenter', handlePointerEnter);
      bar.removeEventListener('mouseleave', handlePointerLeave);
      bar.removeEventListener('focusin', handleFocusIn);
      bar.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pauseButton) pauseButton.removeEventListener('click', toggleUserPause);
      if (motionQuery) {
        if (motionQuery.removeEventListener) motionQuery.removeEventListener('change', handleMotionChange);
        else if (motionQuery.removeListener) motionQuery.removeListener(handleMotionChange);
      }
      document.documentElement.style.removeProperty('--bcj-announcement-height');
      document.documentElement.classList.remove(ROOT_HIDDEN_CLASS);
    });
  };

  const initAll = () => {
    document.querySelectorAll(SELECTOR).forEach(initBar);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
